import React from "react";
import Accordion from "components/odl-v2/Accordion/Accordion";
import AccordionSummary from "components/odl-v2/Accordion/AccordionSummary";
import AccordionDetails from "components/odl-v2/Accordion/AccordionDetails";
import IncomingAccessRequestTable from "components/AccessRequestTable/IncomingAccessRequestTable";
import ParticipantEditDialog from "components/ParticipantEditDialog/ParticipantEditDialog";
import CountBadge from "components/CountBadge/CountBadge";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/react";
import { styled } from "@mui/material/styles";
import { useStoreDispatch, useStoreSelector } from "store/hooks";
import { fetchParticipantTypes } from "store/domain-data/participant-type/participantType";
import { fetchParticipants } from "store/domain-data/participant/participant";
import { usePostHog } from "posthog-js/react";
import { resetParticipantData } from "store/app-state/participant-buffer/participantBuffer";
import { IParticipantPersonalInfoFormData } from "models/Participant.model";
import { unwrapResult } from "@reduxjs/toolkit";
import { useToast } from "hooks/useToast";
import { IAccessRequestEntity } from "models/AccessRequest.model";
import {
  completeAccessRequest,
  rejectAccessRequest,
  selectAllIncomingAccessRequestEntities,
} from "store/domain-data/access-request/accessRequest";

type Props = {};

const IncomingAccessRequestAccordion: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { toastSuccess, toastError } = useToast();
  const dispatch = useStoreDispatch();
  const posthog = usePostHog();

  const [isExpanded, setIsExpanded] = React.useState(true);
  const [isShowParticipantDialog, setIsShowParticipantDialog] = React.useState(false);
  const [accessRequest, setAccessRequest] = React.useState<IAccessRequestEntity>();

  const incomingAccessRequests = useStoreSelector(selectAllIncomingAccessRequestEntities);
  const incomingAccessRequestCount = incomingAccessRequests.length || 0;
  const defaultParticipantDetails: IParticipantPersonalInfoFormData = React.useMemo(() => {
    return {
      email: accessRequest?.requestUser?.email || "",
      firstName: accessRequest?.requestUser?.firstName || "",
      lastName: accessRequest?.requestUser?.lastName || "",
      phoneCountryCode: accessRequest?.requestUser?.countryCode || "",
      phone: accessRequest?.requestUser?.phoneNumber || "",
      organisation: accessRequest?.requestUser?.organisation || "",
      groupList: "",
      groupName: "",
      fullAddress: accessRequest?.requestUser?.address.fullAddress || "",
      address1: accessRequest?.requestUser?.address.address1 || "",
      address2: accessRequest?.requestUser?.address.address2 || "",
      city: accessRequest?.requestUser?.address.city || "",
      state: accessRequest?.requestUser?.address.state || "",
      country: accessRequest?.requestUser?.address.country || "",
      zipCode: accessRequest?.requestUser?.address.zipCode || "",
      isManualAddress: accessRequest?.requestUser?.address.isManualAddress || false,
    };
  }, [accessRequest]);

  const handleChangeExpanded = (event: React.SyntheticEvent, expanded: boolean) => {
    setIsExpanded(expanded);
  };

  const handleClickAcceptInvitation = React.useCallback(
    async (accessRequest: IAccessRequestEntity) => {
      // Preload participant types for the application
      await dispatch(fetchParticipantTypes(accessRequest?.applicationId || 0));
      await dispatch(fetchParticipants({ applicationId: accessRequest?.applicationId || 0 }));
      posthog?.capture("user access-approved", { uuid: accessRequest?.requestGuid || "" });
      dispatch(resetParticipantData());

      setAccessRequest(accessRequest);
      setIsShowParticipantDialog(true);
    },
    [dispatch, posthog]
  );

  const handleClickDeclineInvitation = React.useCallback(
    async (accessRequest: IAccessRequestEntity) => {
      try {
        await dispatch(rejectAccessRequest(accessRequest?.requestGuid || "")).then(unwrapResult);
        toastSuccess(t(`Access request declined`));
        posthog?.capture("user access-declined", { uuid: accessRequest?.requestGuid || "" });
        setIsShowParticipantDialog(false);
      } catch (e) {
        toastError(e);
      }
    },
    [dispatch, posthog, t, toastError, toastSuccess]
  );

  const handleOnCloseParticipantEdit = React.useCallback(
    (created?: boolean) => {
      if (created) {
        dispatch(completeAccessRequest(accessRequest?.requestGuid || ""));
      }
      setIsShowParticipantDialog(false);
    },
    [accessRequest, dispatch]
  );

  return (
    <React.Fragment>
      <StyledAccordion expanded={isExpanded} onChange={handleChangeExpanded}>
        <AccordionSummary>
          <Typography variant="h3">{t(`Access requests to review`)}</Typography>
          <CountBadge count={incomingAccessRequestCount} data-testid={`Adornment-${incomingAccessRequestCount}`} />
        </AccordionSummary>
        <AccordionDetails>
          <AccessRequestInformation>
            <Typography variant="body2">
              {t(
                `Accepting a request lets you add the person as a project participant. If declined, they’ll be notified—no further action is needed.`
              )}
            </Typography>
            <Typography variant="body2">{t("Requests expire automatically 7 days after being sent.")}</Typography>
          </AccessRequestInformation>

          <IncomingAccessRequestTable
            data={incomingAccessRequests}
            onClickAccept={handleClickAcceptInvitation}
            onClickDecline={handleClickDeclineInvitation}
          />
        </AccordionDetails>
      </StyledAccordion>

      {isShowParticipantDialog && (
        <ParticipantEditDialog
          applicationId={accessRequest?.applicationId || 0}
          onClose={handleOnCloseParticipantEdit}
          allowSearch={false}
          initialPersonalValues={defaultParticipantDetails}
          allowModifyEmail={false}
        />
      )}
    </React.Fragment>
  );
};

const StyledAccordion = styled(Accordion)(
  ({ theme }) => css`
    margin: 0 !important;
    border-radius: ${theme.spacing(2)};
    overflow: hidden;

    .MuiAccordionSummary-root {
      min-height: 56px !important;

      & > .MuiAccordionSummary-content {
        min-height: 56px !important;
        display: flex;

        ${theme.mixins.flexGap("8px")}
      }
    }

    .MuiAccordionDetails-root {
      padding: 0;
    }
  `
);

const AccessRequestInformation = styled("div")(
  ({ theme }) => css`
    padding: 16px 16px 24px 16px;
  `
);

export default IncomingAccessRequestAccordion;
